// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Pan/zoom view controller.
//
// The SVG coordinate system has its origin at the centre of the canvas
// (the contentGroup is translated by width/2, height/2).  Pan and zoom
// work by mutating the <svg viewBox> attribute rather than applying CSS
// transforms, which keeps the coordinate space clean and gives us crisp
// rendering at any scale.
//
// M4 extensions:
//   - createView accepts a callbacks object { onNodeHover, onNodeClick, onViewChange }.
//   - Hover is detected in onPointerMove (when not dragging).
//   - Click vs drag: threshold 3 px; fires onNodeClick from stopDrag.
//   - onViewChange({ scale, viewBox }) fires from applyViewBox (single source).
//   - getScale() / getViewBox() accessors.
//   - Persistent scalebar overlay <g> sibling of contentGroup.
//   - setScalebar(groupOrNull) / replaceHalos(newGroup) / replaceLabels(newGroup|null).
//   - fit() uses contentGroup.getBBox() so the scalebar overlay is excluded.

import { el, clear } from "./dom.js";
import {
  widthForScale,
  recentreViewBox,
  scaleBounds,
  clamp,
  clientToWorld,
} from "./camera.js";

// Movement threshold in client pixels below which a pointer-down/up pair
// is treated as a click rather than a drag.
const CLICK_THRESHOLD = 3;

/**
 * Build and attach a pan/zoom SVG viewer inside `container`.
 *
 * @param {HTMLElement} container - The host element.
 * @param {{ width: number, height: number }} size - Initial logical dimensions.
 * @param {object} [callbacks={}] - Semantic event callbacks.
 * @param {function} [callbacks.onNodeHover] - Called with (id|null, col|null) on hover change; col is the metadata column name when hovering a metadata block rect.
 * @param {function} [callbacks.onNodeClick] - Called with (id|null, { append }) on click.
 * @param {function} [callbacks.onViewChange] - Called with ({ scale, viewBox }) after any viewBox mutation.
 * @returns {{ svg, contentGroup, mount, resize, fit, destroy, getScale, getViewBox, setScalebar, replaceHalos, replaceLabels }}
 */
export function createView(container, size, callbacks = {}) {
  const { onNodeHover, onNodeClick, onViewChange } = callbacks;

  // Internal viewBox state.  x/y are the top-left corner in logical coords;
  // w/h are the visible window size (shrinking = zoom in, growing = zoom out).
  let vb = { x: 0, y: 0, w: size.width, h: size.height };

  // Logical (design) size — updated by resize() so fit() can convert
  // contentGroup-local BBox coords into SVG root user-space without
  // touching screen pixels (getBoundingClientRect).
  let logicalSize = { width: size.width, height: size.height };

  // The fit-to-content scale, captured each time fit() runs. Zoom level is
  // defined relative to it: scale = baseScale * 2 ** zoom, so zoom 0 == fitted.
  let baseScale = 1;

  // ---- Element construction ------------------------------------------------

  const svg = el("svg", {
    width: "100%",
    height: "100%",
    viewBox: formatViewBox(vb),
    preserveAspectRatio: "xMidYMid meet",
    style: "display:block",
  });

  // The content group is translated so that (0,0) in node-coordinate space
  // maps to the visual centre of the canvas.
  const contentGroup = el("g", {
    transform: centreTransform(size.width, size.height),
  });

  // Persistent scalebar overlay — sibling of contentGroup, never cleared by mount().
  // Recomputed on every view change by index.js via setScalebar().
  const scalebarOverlay = el("g", { class: "scalebar-overlay" });

  // User-supplied overlay — arbitrary SVG the embedder injects via setOverlay().
  // Sits above the tree and scalebar but below the branding badge so the logo is
  // never occluded.
  const userOverlay = el("g", { class: "user-overlay" });

  // Branding badge overlay — the bottom-left logo, recomputed on every view
  // change by index.js via setLogo(). Painted last so it always stays on top.
  const logoOverlay = el("g", { class: "logo-overlay" });

  svg.appendChild(contentGroup);
  svg.appendChild(scalebarOverlay);
  svg.appendChild(userOverlay);
  svg.appendChild(logoOverlay);
  container.appendChild(svg);

  // ---- Helpers ------------------------------------------------------------

  function formatViewBox({ x, y, w, h }) {
    return `${x} ${y} ${w} ${h}`;
  }

  function centreTransform(w, h) {
    return `translate(${w / 2},${h / 2})`;
  }

  function computeScale() {
    return svg.clientWidth > 0 ? svg.clientWidth / vb.w : 1;
  }

  function applyViewBox() {
    svg.setAttribute("viewBox", formatViewBox(vb));
    if (typeof onViewChange === "function") {
      onViewChange({ scale: computeScale(), viewBox: { ...vb } });
    }
  }

  // Convert a client-space pointer position to the current viewBox coordinate.
  // Delegates to the pure clientToWorld so the transform has a single,
  // unit-tested source of truth shared with projectPoint/unprojectPoint.
  function clientToViewBox(clientX, clientY) {
    const rect = svg.getBoundingClientRect();
    const [vx, vy] = clientToWorld(vb, rect, [clientX, clientY]);
    return { vx, vy };
  }

  // ---- Zoom (wheel) -------------------------------------------------------

  const MIN_W = size.width * 0.01;
  const MAX_W = size.width * 10;
  const ZOOM_FACTOR = 1.1;

  function onWheel(event) {
    event.preventDefault();

    // Guard against a zero-size element: clientToViewBox would divide by 0 and
    // produce NaN viewBox coordinates, blanking the render.
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const { vx, vy } = clientToViewBox(event.clientX, event.clientY);

    const direction = event.deltaY < 0 ? -1 : 1;
    const factor = direction > 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR;

    const newW = Math.min(MAX_W, Math.max(MIN_W, vb.w * factor));
    const newH = vb.h * (newW / vb.w);

    const fx = (vx - vb.x) / vb.w;
    const fy = (vy - vb.y) / vb.h;
    vb = {
      x: vx - fx * newW,
      y: vy - fy * newH,
      w: newW,
      h: newH,
    };

    applyViewBox();
  }

  // ---- Pan (pointer drag) + hover + click ---------------------------------

  // drag state tracks pan and also serves as the click-vs-drag guard.
  let drag = null;
  // { startClientX, startClientY, startVBX, startVBY, moved: bool }

  // Track last hovered (id, col) to avoid emitting duplicate onNodeHover calls.
  // Keying on both id AND col is required so that moving from a node's shape
  // (col=null) into that same node's metadata block (col set) re-fires the hover
  // and surfaces the metadata-column tooltip.
  let _lastHoverId = undefined;
  let _lastHoverCol = undefined;

  function resolveHit(target) {
    const hit = target && target.closest ? target.closest("[data-node-id]") : null;
    const id  = hit ? hit.getAttribute("data-node-id") : null;
    const col = hit ? hit.getAttribute("data-col") : null;
    return { id, col };
  }

  function resolveNodeId(target) {
    return resolveHit(target).id;
  }

  function onPointerDown(event) {
    if (event.button !== 0) return;
    drag = {
      startClientX: event.clientX,
      startClientY: event.clientY,
      startVBX: vb.x,
      startVBY: vb.y,
      moved: false,
      downTarget: event.target,
    };
    svg.style.cursor = "grabbing";
    event.preventDefault();
  }

  function onPointerMove(event) {
    if (drag) {
      // Pan logic
      const rect = svg.getBoundingClientRect();
      // Guard against a zero-size element (e.g. a hidden/not-yet-laid-out
      // container): dividing the viewBox by 0 yields Infinity/NaN and would
      // silently blank the SVG.
      if (rect.width === 0 || rect.height === 0) return;
      const scaleX = vb.w / rect.width;
      const scaleY = vb.h / rect.height;

      const dx = (event.clientX - drag.startClientX) * scaleX;
      const dy = (event.clientY - drag.startClientY) * scaleY;

      vb = {
        ...vb,
        x: drag.startVBX - dx,
        y: drag.startVBY - dy,
      };

      // Mark drag as having moved if threshold exceeded.
      const moveDistPx = Math.hypot(
        event.clientX - drag.startClientX,
        event.clientY - drag.startClientY,
      );
      if (moveDistPx >= CLICK_THRESHOLD) {
        drag.moved = true;
      }

      applyViewBox();
      return;
    }

    // Not dragging — emit hover events.
    if (typeof onNodeHover === "function") {
      const { id, col } = resolveHit(event.target);
      if (id !== _lastHoverId || col !== _lastHoverCol) {
        _lastHoverId = id;
        _lastHoverCol = col;
        onNodeHover(id, col);
      }
    }
  }

  function stopDrag(event) {
    if (!drag) return;

    const wasDrag = drag.moved;
    const downTarget = drag.downTarget;
    drag = null;
    svg.style.cursor = "";

    // If the pointer did not move enough to be a pan, treat as a click.
    if (!wasDrag && typeof onNodeClick === "function") {
      const id = resolveNodeId(downTarget);
      const append = !!(event && (event.metaKey || event.ctrlKey));
      onNodeClick(id, { append });
    }
  }

  // Pointer leaving the SVG: cancel any in-progress drag WITHOUT promoting it
  // to a click (a press that never releases on the element is not a click),
  // and clear hover highlight/tooltip so a halo ring never lingers after the
  // pointer exits. Resetting _lastHoverId/_lastHoverCol also lets re-entry onto
  // the SAME node re-fire the hover (the dedup would otherwise suppress it).
  function onMouseLeave() {
    if (drag) {
      drag = null;
      svg.style.cursor = "";
    }
    if (
      typeof onNodeHover === "function" &&
      (_lastHoverId != null || _lastHoverCol != null)
    ) {
      _lastHoverId = null;
      _lastHoverCol = null;
      onNodeHover(null, null);
    }
  }

  // ---- Event registration -------------------------------------------------

  svg.addEventListener("wheel", onWheel, { passive: false });
  svg.addEventListener("mousedown", onPointerDown);
  svg.addEventListener("mousemove", onPointerMove);
  svg.addEventListener("mouseup", stopDrag);
  svg.addEventListener("mouseleave", onMouseLeave);

  // ---- Public API ---------------------------------------------------------

  function mount(...groups) {
    clear(contentGroup);
    for (const group of groups) {
      if (group instanceof Node) {
        contentGroup.appendChild(group);
      }
    }
  }

  /**
   * Fit the viewBox to the actual rendered content.
   *
   * Uses contentGroup.getBBox() so the screen-fixed scalebar overlay
   * is excluded and cannot distort auto-fit. The BBox is in contentGroup-local
   * space; the logical centre offset (logicalSize.width/2, logicalSize.height/2)
   * is added to convert to SVG root user-space. getBoundingClientRect() is NOT
   * used — it returns screen pixels which diverge from logical units when the
   * element is scaled by CSS or device pixel ratio.
   *
   * No-ops when the content box is empty (width or height 0).
   *
   * @param {number} margin - Padding in user units added on every side.
   */
  function fit(margin = 12) {
    let box;
    try {
      box = contentGroup.getBBox();
    } catch {
      return;
    }
    if (!box || box.width === 0 || box.height === 0) return;

    // contentGroup.getBBox() returns coordinates in contentGroup-LOCAL space
    // (around the origin, before the translate(width/2, height/2) transform).
    // Converting to the SVG root user-space (where the viewBox lives) requires
    // ADDING the logical centre offset. We use logicalSize — the design-space
    // dimensions — not getBoundingClientRect(), which returns screen pixels and
    // would be wrong whenever rendered size differs from logical size.
    vb = {
      x: box.x + logicalSize.width / 2 - margin,
      y: box.y + logicalSize.height / 2 - margin,
      w: box.width + 2 * margin,
      h: box.height + 2 * margin,
    };
    applyViewBox();
    baseScale = computeScale();
  }

  function resize(width, height) {
    vb = { x: 0, y: 0, w: width, h: height };
    logicalSize = { width, height };
    applyViewBox();
    contentGroup.setAttribute("transform", centreTransform(width, height));
  }

  /**
   * Replace the contents of the persistent scalebar overlay.
   *
   * @param {SVGGElement|null} group - New <g class="scalebar"> element, or null to clear.
   */
  function setScalebar(group) {
    clear(scalebarOverlay);
    if (group instanceof Node) {
      scalebarOverlay.appendChild(group);
    }
  }

  /**
   * Replace the contents of the persistent user overlay.
   *
   * @param {SVGElement|null} group - Arbitrary overlay element, or null to clear.
   */
  function setOverlay(group) {
    clear(userOverlay);
    if (group instanceof Node) {
      userOverlay.appendChild(group);
    }
  }

  /**
   * Replace the contents of the persistent branding-badge overlay.
   *
   * @param {SVGGElement|null} group - New <g class="logo"> element, or null to clear.
   */
  function setLogo(group) {
    clear(logoOverlay);
    if (group instanceof Node) {
      logoOverlay.appendChild(group);
    }
  }

  /**
   * Swap the <g class="halos"> element inside contentGroup in-place,
   * preserving z-order.
   *
   * @param {SVGGElement} newGroup - Replacement <g class="halos"> element.
   */
  function replaceHalos(newGroup) {
    const existing = contentGroup.querySelector(".halos");
    if (existing) {
      contentGroup.replaceChild(newGroup, existing);
    } else {
      // Fallback: append at end (should not happen after first render).
      contentGroup.appendChild(newGroup);
    }
  }

  /**
   * Swap the <g class="leaf-labels"> element inside contentGroup in-place,
   * preserving z-order. Null newGroup is a no-op (label presence/absence is
   * managed by full render()).
   *
   * @param {SVGGElement|null} newGroup
   */
  function replaceLabels(newGroup) {
    if (newGroup == null) return;
    const existing = contentGroup.querySelector(".leaf-labels");
    if (existing) {
      contentGroup.replaceChild(newGroup, existing);
    } else {
      contentGroup.appendChild(newGroup);
    }
  }

  /** @returns {number} Current view scale: svg.clientWidth / viewBox.w */
  function getScale() {
    return computeScale();
  }

  /** @returns {{ x, y, w, h }} Copy of current viewBox state */
  function getViewBox() {
    return { ...vb };
  }

  /** @returns {number} Fit-to-content scale captured at the last fit(). */
  function getBaseScale() {
    return baseScale;
  }

  /**
   * Replace the viewBox outright (used by setView / session restore). Ignores
   * non-finite or non-positive-area inputs so a bad value can never blank the
   * render.
   * @param {{ x:number, y:number, w:number, h:number }} next
   */
  function setViewBox(next) {
    if (!next) return;
    const { x, y, w, h } = next;
    if (![x, y, w, h].every(Number.isFinite) || w <= 0 || h <= 0) return;
    vb = { x, y, w, h };
    applyViewBox();
  }

  /**
   * Zoom so content renders at `scale` (pixels per logical unit) about a
   * normalized anchor (default centre). Width is clamped to the same
   * [MIN_W, MAX_W] bounds the wheel uses. No-op without a measured width.
   * @param {number} scale
   * @param {[number, number]} [anchor=[0.5,0.5]]
   */
  function zoomTo(scale, anchor = [0.5, 0.5]) {
    const cw = svg.clientWidth;
    if (!(cw > 0) || !(scale > 0)) return;
    const targetW = clamp(widthForScale(cw, scale), MIN_W, MAX_W);
    const targetH = vb.h * (targetW / vb.w);
    vb = recentreViewBox(vb, targetW, targetH, anchor);
    applyViewBox();
  }

  /** @returns {{ minScale:number, maxScale:number }} from the MIN_W/MAX_W bounds. */
  function getScaleBounds() {
    return scaleBounds(svg.clientWidth || 0, MIN_W, MAX_W);
  }

  function destroy() {
    svg.removeEventListener("wheel", onWheel);
    svg.removeEventListener("mousedown", onPointerDown);
    svg.removeEventListener("mousemove", onPointerMove);
    svg.removeEventListener("mouseup", stopDrag);
    svg.removeEventListener("mouseleave", onMouseLeave);
    if (svg.parentNode) {
      svg.parentNode.removeChild(svg);
    }
  }

  return {
    svg,
    contentGroup,
    mount,
    resize,
    fit,
    destroy,
    getScale,
    getViewBox,
    getBaseScale,
    setViewBox,
    zoomTo,
    getScaleBounds,
    setScalebar,
    setOverlay,
    setLogo,
    replaceHalos,
    replaceLabels,
  };
}
